import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DatasetActions, datasetsSelector } from 'Redux/thunks/datasets.thunks';
import { PipeActions } from '../redux-store/thunks/pipes.thunks';

import { useDatasetsListColumns } from '../hooks/useDatasetsListColumns.hook';

import {
  defaultSortBy,
  defaultVisibleCols,
  defaultVisibleFacets,
  filterLabelMapping,
  emptyFilterDatasets,
  getSystemsDefaultFilter,
} from '../constants/datasetsListTableDefaults';
import { getDatasetsWithGlobal } from '../utils/datasets.utils';
import { shouldRenderRefreshButton } from '../utils/common.utils';

import { DatahubSettings } from './DatahubSettings.container';

import FilterTable from '../components/filter-table/FilterTable';
import { LoadingPanel } from '../components/common/LoadingPanel';
import { ListActions } from '../components/common/ListActions.view';

import type { RootState } from '../types/state.types';

export const UserDatasets = () => {
  const dispatch = useDispatch();

  const datasets = useSelector((state: RootState) =>
    getDatasetsWithGlobal(datasetsSelector(state), state.pipes, state.upstreams)
  );
  const pipes = useSelector((state: RootState) => state.pipes);
  const upstreams = useSelector((state: RootState) => state.upstreams);
  const datasetsLoaded = useSelector((state: RootState) => state.loadStatus.datasetsLoaded);
  const columns = useDatasetsListColumns(pipes, upstreams, 'user-datasets');
  const isLiveUpdatesEnabled = useSelector((state: RootState) => state.globals.enableLiveUpdates);
  const isLiveUpdatesSupported = useSelector(
    (state: RootState) => state.subscription.supportsLiveUpdates ?? false
  );
  const failedToConnectToLiveUpdates = useSelector(
    (state: RootState) => state.subscription.failedToConnectToLiveUpdates
  );

  const renderActions = (selectedIds: string[], clearSelection: () => void) => {
    const selectedDatasets = getSelectedDatasets(selectedIds);

    const deleteDatasets = () => {
      onDelete(selectedIds);
      clearSelection();
    };

    return <ListActions selection={selectedDatasets} onDelete={deleteDatasets} name="dataset" />;
  };

  /*
    OBS! deleting "user dataset", is actually deleting a pipe
  */
  const deleteDataset = (id: string) => {
    dispatch(DatasetActions.delete(id));
  };

  const loadAllForced = () => dispatch(DatasetActions.loadAllForced());

  const onDelete = (selectedIds: string[]) => {
    dispatch(PipeActions.delete(selectedIds));
    selectedIds.forEach((datasetId) => deleteDataset(datasetId));
  };

  const getSelectedDatasets = (selectedIds: string[]) =>
    datasets.filter((dataset) => selectedIds.includes(dataset._id));

  const handleFilterChanged = useCallback(() => {
    loadAllForced();
  }, [loadAllForced]);

  if (datasetsLoaded === false) {
    return <LoadingPanel loadingMessage="Loading user datasets..." />;
  }

  return (
    // @ts-ignore
    <DatahubSettings
      includeInternal={false}
      shouldRenderRefreshButton={shouldRenderRefreshButton({
        isLiveUpdatesSupported,
        isLiveUpdatesEnabled,
        failedToConnectToLiveUpdates,
      })}
    >
      <FilterTable
        actionsRenderer={renderActions}
        defaults={{
          filter: getSystemsDefaultFilter('user'),
          sortBy: defaultSortBy,
          visibleCols: defaultVisibleCols,
          visibleFacets: defaultVisibleFacets,
        }}
        emptyFilter={emptyFilterDatasets}
        filterLabelMapping={filterLabelMapping}
        columns={columns}
        itemsToFilter={datasets}
        tableKey="userDatasets"
        onFilterChanged={handleFilterChanged}
      />
    </DatahubSettings>
  );
};
